// =============================================================================
// Channel Repository - CRUD pour les channels chat
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { ChatChannel, ChatScopeType } from '../types';

export async function fetchChannels(orgId: string, currentMemberId?: string | null): Promise<ChatChannel[]> {
  const { data, error } = await supabase
    .from('chat_channels')
    .select(`
      *,
      chat_channel_members(
        org_member_id,
        is_pinned,
        member:org_member_id(
          id,
          user_id,
          core_profiles:user_id(full_name, avatar_url, email)
        )
      )
    `)
    .eq('org_id', orgId)
    .eq('is_archived', false)
    .order('is_dm', { ascending: true })
    .order('name');

  if (error) throw error;

  // Hydrate DM display names and scope info
  const channels = (data || []).map((ch: any) => {
    const channel = { ...ch } as ChatChannel;
    
    // Hydrate pinned state and deleted_at for current user
    if (currentMemberId && ch.chat_channel_members) {
      const myMembership = ch.chat_channel_members.find(
        (m: any) => m.org_member_id === currentMemberId
      );
      (channel as any)._pinned = myMembership?.is_pinned ?? false;
      (channel as any)._deleted_at = myMembership?.deleted_at ?? null;
    }
    
    // For 1:1 DMs: find the other member's profile and use as display name
    // Group conversations (is_dm=true but >2 members) keep their original name
    const memberCount = ch.chat_channel_members?.length || 0;
    (channel as any)._member_count = memberCount;
    if (ch.is_dm && currentMemberId && ch.chat_channel_members && memberCount <= 2) {
      const otherMember = ch.chat_channel_members.find(
        (m: any) => m.org_member_id !== currentMemberId
      );
      if (otherMember?.member?.core_profiles) {
        const p = Array.isArray(otherMember.member.core_profiles) 
          ? otherMember.member.core_profiles[0] 
          : otherMember.member.core_profiles;
        if (p) {
          channel.name = p.full_name || p.email || 'Utilisateur';
          channel._dm_profile = { full_name: p.full_name ?? null, avatar_url: p.avatar_url ?? null, email: p.email ?? '', org_member_id: otherMember.org_member_id };
        }
      }
    }
    
    // Clean up joined data from the channel object
    delete (channel as any).chat_channel_members;
    return channel;
  });

  // Hydrate scope names for space/project channels
  const spaceIds = channels.filter(c => c.scope_type === 'space' && c.scope_id).map(c => c.scope_id!);
  const projectIds = channels.filter(c => c.scope_type === 'project' && c.scope_id).map(c => c.scope_id!);

  const [spaceNames, projectNames] = await Promise.all([
    spaceIds.length > 0
      ? supabase.from('project_spaces').select('id, name').in('id', spaceIds).then(r => r.data || [])
      : Promise.resolve([]),
    projectIds.length > 0
      ? supabase.from('project_projects').select('id, name').in('id', projectIds).then(r => r.data || [])
      : Promise.resolve([]),
  ]);

  const scopeMap = new Map<string, string>();
  spaceNames.forEach((s: any) => scopeMap.set(s.id, s.name));
  projectNames.forEach((p: any) => scopeMap.set(p.id, p.name));

  channels.forEach(c => {
    if (c.scope_id && scopeMap.has(c.scope_id)) {
      c.scope_name = scopeMap.get(c.scope_id)!;
    }
  });

  // Fetch last message time per channel for sorting + filter empty DMs
  const allChannelIds = channels.map(c => c.id);
  if (allChannelIds.length > 0) {
    const { data: lastMessages } = await supabase
      .from('chat_messages')
      .select('channel_id, created_at')
      .in('channel_id', allChannelIds)
      .is('parent_message_id', null)
      .order('created_at', { ascending: false });

    // Build map: channel_id -> latest message created_at
    const lastMsgMap = new Map<string, string>();
    for (const m of (lastMessages || [])) {
      if (!lastMsgMap.has(m.channel_id)) {
        lastMsgMap.set(m.channel_id, m.created_at);
      }
    }

    // Hydrate last_message timestamp for sorting
    channels.forEach(c => {
      const lastAt = lastMsgMap.get(c.id);
      if (lastAt) {
        c.last_message = { created_at: lastAt } as any;
      }
    });

    // Filter out empty 1:1 DMs for recipients. The sender can keep the draft
    // conversation open locally, but it should appear for the other person only
    // after the first message exists.
    // Also filter out conversations where user has soft-deleted AND no new messages since
    const now = Date.now();
    return channels.filter(c => {
      const memberCount = (c as any)._member_count as number | undefined;
      const isGroupConversation = c.is_dm && ((memberCount && memberCount > 2) || (!!c.name && c.name !== 'DM'));

      // Hide only empty pure 1:1 DMs — keep a short-lived draft only for creator.
      if (c.is_dm && !isGroupConversation && !lastMsgMap.has(c.id)) {
        const createdAgo = now - new Date(c.created_at).getTime();
        if (c.created_by !== currentMemberId || createdAgo > 2 * 60 * 1000) return false;
      }
      // Hide conversations soft-deleted by user if no new messages since deletion
      if (c.is_dm && (c as any)._deleted_at) {
        const lastMsg = lastMsgMap.get(c.id);
        if (!lastMsg || new Date(lastMsg) <= new Date((c as any)._deleted_at)) return false;
      }
      // Hide creator-deleted groups for the creator
      if (c.is_dm && c.creator_deleted_at && currentMemberId && c.created_by === currentMemberId) return false;
      return true;
    }) as ChatChannel[];
  }

  return channels as ChatChannel[];
}

export async function fetchChannelsByScope(
  orgId: string, 
  scopeType: ChatScopeType, 
  scopeId: string
): Promise<ChatChannel[]> {
  const { data, error } = await supabase
    .from('chat_channels')
    .select('*')
    .eq('org_id', orgId)
    .eq('scope_type', scopeType)
    .eq('scope_id', scopeId)
    .eq('is_archived', false)
    .order('is_default', { ascending: false })
    .order('name');

  if (error) throw error;
  return (data || []) as ChatChannel[];
}

export async function fetchDMChannels(orgId: string, memberIds: string[]): Promise<ChatChannel[]> {
  // Fetch DM channels where user is a member
  const { data, error } = await supabase
    .from('chat_channels')
    .select(`
      *,
      chat_channel_members!inner(org_member_id)
    `)
    .eq('org_id', orgId)
    .eq('is_dm', true)
    .in('chat_channel_members.org_member_id', memberIds);

  if (error) throw error;
  return (data || []) as ChatChannel[];
}

export async function createChannel(params: {
  orgId: string;
  scopeType: ChatScopeType;
  scopeId?: string | null;
  name: string;
  description?: string;
  isDm?: boolean;
  createdBy?: string;
  icon?: string;
}): Promise<ChatChannel> {
  const { data, error } = await supabase.rpc('create_chat_channel', {
    p_org_id: params.orgId,
    p_scope_type: params.scopeType,
    p_scope_id: params.scopeId || null,
    p_name: params.name,
    p_description: params.description || null,
    p_is_dm: params.isDm || false,
    p_created_by: params.createdBy || null,
    p_icon: params.icon || null,
  });

  if (error) throw error;

  return {
    id: data as string,
    org_id: params.orgId,
    scope_type: params.scopeType,
    scope_id: params.scopeId || null,
    name: params.name,
    description: params.description || null,
    is_dm: params.isDm || false,
    is_default: false,
    is_archived: false,
    write_policy: 'default' as any,
    created_by: params.createdBy || null,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    icon: params.icon || null,
  } as ChatChannel;
}

export async function updateChannel(
  channelId: string,
  updates: Partial<Pick<ChatChannel, 'name' | 'description' | 'is_archived'>>
): Promise<void> {
  const { error } = await supabase
    .from('chat_channels')
    .update(updates)
    .eq('id', channelId);

  if (error) throw error;
}

export async function deleteChannel(channelId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_channels')
    .delete()
    .eq('id', channelId);

  if (error) throw error;
}

// DM: find or create a DM channel between two members (uses SECURITY DEFINER RPC)
export async function findOrCreateDM(
  orgId: string,
  myMemberId: string,
  otherMemberId: string
): Promise<ChatChannel> {
  const { data, error } = await supabase.rpc('find_or_create_dm', {
    p_org_id: orgId,
    p_my_member_id: myMemberId,
    p_other_member_id: otherMemberId,
  });

  if (error) throw error;

  // Return a minimal ChatChannel object with the ID
  return {
    id: data as string,
    org_id: orgId,
    scope_type: 'workspace',
    name: 'DM',
    is_dm: true,
    is_default: false,
    is_archived: false,
    created_by: myMemberId,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    description: null,
    scope_id: null,
  } as ChatChannel;
}

// Pin/unpin channel
export async function togglePinChannel(channelId: string, memberId: string, isPinned: boolean): Promise<void> {
  const { error } = await supabase
    .from('chat_channel_members')
    .update({ is_pinned: isPinned })
    .eq('channel_id', channelId)
    .eq('org_member_id', memberId);

  if (error) throw error;
}

// Update notification preference for a channel
export type NotificationPref = 'all' | 'mentions' | 'none';

export async function updateNotificationPref(
  channelId: string,
  memberId: string,
  pref: NotificationPref
): Promise<void> {
  const { error } = await supabase
    .from('chat_channel_members')
    .upsert({
      channel_id: channelId,
      org_member_id: memberId,
      notification_pref: pref,
    }, { onConflict: 'channel_id,org_member_id' });

  if (error) throw error;
}

// Get notification preference for a channel
export async function getNotificationPref(
  channelId: string,
  memberId: string
): Promise<NotificationPref> {
  const { data } = await supabase
    .from('chat_channel_members')
    .select('notification_pref')
    .eq('channel_id', channelId)
    .eq('org_member_id', memberId)
    .maybeSingle();

  return (data?.notification_pref as NotificationPref) || 'all';
}

// Create a group conversation (multi-member DM-like channel)
export async function createGroupConversation(params: {
  orgId: string;
  name: string;
  memberIds: string[];
  createdBy: string;
}): Promise<ChatChannel> {
  // Use RPC to create channel (bypasses RLS)
  const { data: channelId, error } = await supabase.rpc('create_chat_channel', {
    p_org_id: params.orgId,
    p_scope_type: 'workspace',
    p_scope_id: null,
    p_name: params.name,
    p_description: null,
    p_is_dm: true,
    p_created_by: params.createdBy,
    p_icon: null,
  });

  if (error) throw error;

  // Add additional members (creator already added by RPC)
  const otherMembers = params.memberIds.filter(mid => mid !== params.createdBy);
  if (otherMembers.length > 0) {
    const membersToInsert = otherMembers.map(mid => ({
      channel_id: channelId as string,
      org_member_id: mid,
    }));

    const { error: membersError } = await supabase
      .from('chat_channel_members')
      .insert(membersToInsert);

    if (membersError) {
      console.error('Error adding members to group channel:', membersError);
    }
  }

  return {
    id: channelId as string,
    org_id: params.orgId,
    scope_type: 'workspace',
    scope_id: null,
    name: params.name,
    is_dm: true,
    is_default: false,
    is_archived: false,
    write_policy: 'default' as any,
    created_by: params.createdBy,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    description: null,
  } as ChatChannel;
}

// Mark channel as read
export async function markChannelRead(channelId: string, memberId: string): Promise<void> {
  const { error } = await (supabase as any).rpc('mark_chat_channel_read', {
    p_channel_id: channelId,
    p_member_id: memberId,
  });

  if (!error) return;

  // Staging/prod may not yet have the new RPC in their schema cache. Fall back
  // to the previous direct update/insert path so sending/opening messages never
  // fails with a 404 while the DB catches up.
  if (error.code !== 'PGRST202') {
    console.warn('[markChannelRead] failed:', error.message);
    return;
  }

  const now = new Date().toISOString();
  const { data: existing, error: selectError } = await supabase
    .from('chat_channel_members')
    .select('channel_id')
    .eq('channel_id', channelId)
    .eq('org_member_id', memberId)
    .maybeSingle();

  if (selectError) {
    console.warn('[markChannelRead] fallback select failed:', selectError.message);
    return;
  }

  if (existing) {
    const { error: updateError } = await supabase
      .from('chat_channel_members')
      .update({ last_read_at: now })
      .eq('channel_id', channelId)
      .eq('org_member_id', memberId);
    if (updateError) console.warn('[markChannelRead] fallback update failed:', updateError.message);
    return;
  }

  const { error: insertError } = await supabase
    .from('chat_channel_members')
    .insert({ channel_id: channelId, org_member_id: memberId, last_read_at: now });

  if (insertError && insertError.code !== '42501') {
    console.warn('[markChannelRead] fallback insert failed:', insertError.message);
  }
}

// ─── SCOPES FOR CHANNEL CREATION ─────────────────────────────────────────────

export async function fetchChannelScopes(orgId: string): Promise<{
  spaces: { id: string; name: string }[];
  projects: { id: string; name: string }[];
}> {
  const [spacesRes, projectsRes] = await Promise.all([
    supabase.from('project_spaces').select('id, name').eq('org_id', orgId).eq('is_archive', false).order('name'),
    supabase.from('project_projects').select('id, name').eq('org_id', orgId).is('original_space_id', null).neq('status', 'archived').order('name'),
  ]);
  return {
    spaces: spacesRes.data || [],
    projects: projectsRes.data || [],
  };
}

// ─── MEMBER ID RESOLUTION ────────────────────────────────────────────────────

export async function resolveCurrentMemberId(orgId: string): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('core_organization_members')
    .select('id')
    .eq('org_id', orgId)
    .eq('user_id', user.id)
    .eq('status', 'active')
    .single();
  return data?.id || null;
}

// ─── UNREAD COUNTS ───────────────────────────────────────────────────────────

export async function fetchUnreadCounts(orgId: string, memberId: string): Promise<Record<string, number> | null> {
  const { data, error } = await supabase.rpc('get_chat_unread_counts', {
    p_org_id: orgId,
    p_member_id: memberId,
  });
  if (error) {
    console.error('Error loading unread counts:', error);
    return null;
  }
  const counts: Record<string, number> = {};
  if (data && typeof data === 'object') {
    for (const [key, val] of Object.entries(data as Record<string, number>)) {
      counts[key] = Number(val) || 0;
    }
  }
  return counts;
}

// ─── SOFT DELETE CONVERSATION ────────────────────────────────────────────────

/**
 * Soft-delete a conversation for the current user.
 * Sets deleted_at on chat_channel_members — messages before this date are hidden.
 */
export async function softDeleteConversation(channelId: string, memberId: string): Promise<void> {
  const { error } = await supabase
    .from('chat_channel_members')
    .update({ deleted_at: new Date().toISOString() })
    .eq('channel_id', channelId)
    .eq('org_member_id', memberId);

  if (error) throw error;
}

/**
 * Creator deletes a group conversation.
 * Marks channel with creator_deleted_at and creator name.
 * The channel remains visible to other members (read-only with a notice).
 */
export async function creatorDeleteGroup(channelId: string, creatorName: string): Promise<void> {
  const { error } = await supabase
    .from('chat_channels')
    .update({
      creator_deleted_at: new Date().toISOString(),
      creator_deleted_by_name: creatorName,
    })
    .eq('id', channelId);

  if (error) throw error;
}

/**
 * Get the deleted_at timestamp for a user's membership in a channel.
 */
export async function getMemberDeletedAt(channelId: string, memberId: string): Promise<string | null> {
  const { data } = await supabase
    .from('chat_channel_members')
    .select('deleted_at')
    .eq('channel_id', channelId)
    .eq('org_member_id', memberId)
    .maybeSingle();

  return data?.deleted_at || null;
}
